import type { ReactNode } from 'react';
import { errorText, type Json, type Source } from '../api.ts';
import { Icon } from './Icon.tsx';

/* ---------- loading / empty / error ---------- */

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="state empty">{children}</div>;
}

export function ErrorBox({ error, onRetry, title = 'Something went wrong' }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div className="errbox" role="alert">
      <Icon name="alert" />
      <div>
        <b>{title}</b>
        <div className="mono">{errorText(error)}</div>
      </div>
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          <Icon name="sync" />
          Retry
        </button>
      )}
    </div>
  );
}

/** Inline error for an action (button row). */
export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <span className="inline-err" role="alert">
      <Icon name="alert" />
      {errorText(error)}
    </span>
  );
}

/* ---------- pills and badges ---------- */

export type Tone = 'ok' | 'bad' | 'warn' | 'info' | 'ai' | 'neutral';

export function Pill({ tone = 'neutral', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return (
    <span className={`pill ${tone === 'neutral' ? '' : tone}`} title={title}>
      {children}
    </span>
  );
}

const STATUS_TONES: Record<string, Tone> = {
  active: 'ok',
  online: 'ok',
  parsed: 'ok',
  applied: 'ok',
  confirmed: 'ok',
  ok: 'ok',
  done: 'ok',
  resolved: 'ok',
  previewed: 'info',
  draft: 'info',
  applying: 'info',
  pending: 'warn',
  held: 'warn',
  open: 'warn',
  onboarding: 'warn',
  suspended: 'warn',
  unauthorized: 'warn',
  quarantined: 'warn',
  unparsed: 'warn',
  skipped: 'neutral',
  unknown: 'neutral',
  retired: 'neutral',
  cancelled: 'neutral',
  discarded: 'neutral',
  inactive: 'neutral',
  rejected: 'bad',
  failed: 'bad',
  offline: 'bad',
  terminated: 'bad',
  error: 'bad',
  dead: 'bad',
};

export function toneFor(status: string | null | undefined): Tone {
  if (!status) return 'neutral';
  return STATUS_TONES[status.toLowerCase()] ?? 'neutral';
}

export function StatusPill({ status, title }: { status: string | null | undefined; title?: string }) {
  if (!status) return <span className="dim">—</span>;
  const tone = toneFor(status);
  return (
    <Pill tone={tone} title={title}>
      <i className="dot" aria-hidden="true" />
      {status.replace(/_/g, ' ')}
    </Pill>
  );
}

const WARNING_FLAGS = new Set(['unmapped_camera', 'unknown_site', 'clock_skew', 'rate_limited', 'unparsed']);
const FLAG_TEXT: Record<string, string> = {
  unmapped_camera: 'The email named a camera that is not mapped to this site',
  unknown_site: 'The recipient address is not linked to a known site',
  clock_skew: 'The vendor time is far from the time we received it',
  rate_limited: 'The sender exceeded the ingest rate limit',
  unparsed: 'No parser template matched this message',
  outside_schedule: 'Received outside contracted monitoring hours',
  received_paused: 'Received while the site’s alarms were paused in the ERP: kept, no event raised',
  guard_tour: 'Found by a scheduled guard tour (the vision worker walked the site cameras)',
};

export function FlagPill({ flag }: { flag: string }) {
  return (
    <Pill tone={WARNING_FLAGS.has(flag) ? 'warn' : flag === 'guard_tour' ? 'ai' : 'neutral'} title={FLAG_TEXT[flag]}>
      {flag.replace(/_/g, ' ')}
    </Pill>
  );
}

export function Flags({ flags }: { flags: string[] }) {
  if (!flags.length) return <span className="dim">—</span>;
  return (
    <span className="chips">
      {flags.map((f) => (
        <FlagPill key={f} flag={f} />
      ))}
    </span>
  );
}

export function SourceBadge({ source }: { source: Source | string }) {
  const erp = source === 'erp';
  return (
    <span
      className={`own ${erp ? 'erp' : 'imp'}`}
      title={erp ? 'Synced from the EyeQ ERP' : 'Loaded from an import file (until Phase 6 ERP sync)'}
    >
      {erp ? 'ERP' : 'IMPORT'}
    </span>
  );
}

export function Dot({ on, title }: { on: boolean | null; title?: string }) {
  return <i className={`hdot ${on === null ? 'unk' : on ? 'on' : 'off'}`} title={title} aria-label={title} role="img" />;
}

/* ---------- read-only ERP fields ---------- */

export function RoField({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={`ro${wide ? ' wide' : ''}`}>
      <span className="lbl">{label}</span>
      <span className="v">
        <span className="vv">{children ?? <span className="dim">—</span>}</span>
        <Icon name="lock" title="Owned by the ERP; read-only here" />
      </span>
    </div>
  );
}

export function ErpLink({ url, label = 'Edit in ERP', compact }: { url: string | null | undefined; label?: string; compact?: boolean }) {
  if (!url) {
    return compact ? null : (
      <span className="btn" aria-disabled="true" title="No ERP link in the record">
        <Icon name="ext" />
        {label}
      </span>
    );
  }
  return (
    <a className={`btn${compact ? ' sm' : ''}`} href={url} target="_blank" rel="noopener noreferrer">
      <Icon name="ext" />
      {label}
    </a>
  );
}

/* ---------- misc ---------- */

export function JsonBlock({ value, maxHeight }: { value: Json | unknown; maxHeight?: number }) {
  let text: string;
  try {
    text = JSON.stringify(value, null, 2) ?? 'undefined';
  } catch {
    text = String(value);
  }
  return (
    <pre className="json" style={maxHeight ? { maxHeight } : undefined}>
      {text}
    </pre>
  );
}

export function Card({ title, right, children, id }: { title: ReactNode; right?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className="card2" aria-labelledby={id}>
      <div className="card-h">
        <h3 id={id}>{title}</h3>
        {right && <div className="card-r">{right}</div>}
      </div>
      {children}
    </section>
  );
}

export function Seg<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={o.value === value ? 'on' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function shortId(id: string | null | undefined, n = 8): string {
  if (!id) return '—';
  return id.length > n + 2 ? `${id.slice(0, n)}…` : id;
}
